import { lazy, Suspense, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { Toaster } from '@/components/ui/Toast';
import { useCommandPalette } from '@/hooks/useCommandPalette';
import { useNotificationSocket } from '@/hooks/useNotificationSocket';
import { useRequirentePortal } from '@/hooks/useRequirentePortal';
import { readStoredSidebarCollapsed } from '@/hooks/useSidebar';
import { useAppDispatch } from '@/store/hooks';
import { uiActions } from '@/store/slices/uiSlice';
import { Sidebar } from './Sidebar';
import { MobileSidebar } from './MobileSidebar';
import { Topbar } from './Topbar';

const LazyCommandPalette = lazy(() =>
  import('@/components/shared/CommandPalette').then(mod => ({
    default: mod.CommandPaletteView,
  }))
);

function CommandPaletteGate() {
  const palette = useCommandPalette();
  if (!palette.isOpen) {
    return null;
  }
  return (
    <Suspense fallback={null}>
      <LazyCommandPalette palette={palette} />
    </Suspense>
  );
}

export function AppShell() {
  const { isRequirenteOnly } = useRequirentePortal();
  useNotificationSocket({ enabled: !isRequirenteOnly });
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(uiActions.setSidebarCollapsed(readStoredSidebarCollapsed()));
  }, [dispatch]);

  return (
    <div className='flex h-screen overflow-hidden'>
      <Sidebar />
      <MobileSidebar />
      <div className='flex min-w-0 flex-1 flex-col overflow-hidden'>
        <Topbar />
        <main className='min-w-0 flex-1 overflow-x-auto overflow-y-auto bg-muted/30 p-4 md:p-6'>
          <Outlet />
        </main>
      </div>
      <Toaster />
      {!isRequirenteOnly && <CommandPaletteGate />}
    </div>
  );
}
